import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * CodeBlock - Syntax-highlighted code block component
 * Satisfies F1 (PRD): "Syntax highlighting for code blocks (use react-syntax-highlighter)"
 *
 * Props:
 *   code     - The code string to display
 *   language - Language identifier (default: 'python')
 */
export function CodeBlock({ code, language = 'python' }) {
  if (!code) return null;
  return (
    <SyntaxHighlighter
      language={language}
      style={vscDarkPlus}
      customStyle={{
        borderRadius: '0.5rem',
        fontSize: '0.875rem',
        margin: '0.5rem 0',
        padding: '1rem',
      }}
      showLineNumbers={code.split('\n').length > 3}
    >
      {String(code).trimEnd()}
    </SyntaxHighlighter>
  );
}

/**
 * ContentWithCode
 * Renders mixed content (text + fenced code blocks) with proper syntax highlighting.
 * Replaces raw dangerouslySetInnerHTML for content strings that may contain ```lang\n...\n```
 */
export function ContentWithCode({ content, className = '' }) {
  if (!content) return null;

  // Split on fenced code blocks: ```language\n...code...\n```
  const fence = /```(\w*)\n([\s\S]*?)```/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = fence.exec(content)) !== null) {
    // Text before the fence
    if (match.index > lastIndex) {
      const text = content.slice(lastIndex, match.index);
      parts.push({ type: 'html', value: text });
    }
    // Code block
    parts.push({ type: 'code', lang: match[1] || 'python', value: match[2] });
    lastIndex = match.index + match[0].length;
  }

  // Trailing text after last fence
  if (lastIndex < content.length) {
    parts.push({ type: 'html', value: content.slice(lastIndex) });
  }

  // If no fences found, render as plain HTML
  if (parts.length === 0) {
    return (
      <div
        className={className}
        dangerouslySetInnerHTML={{ __html: content }}
        style={{ whiteSpace: 'pre-wrap' }}
      />
    );
  }

  return (
    <div className={className}>
      {parts.map((part, i) =>
        part.type === 'code' ? (
          <CodeBlock key={i} code={part.value} language={part.lang || 'python'} />
        ) : (
          <div
            key={i}
            dangerouslySetInnerHTML={{ __html: part.value }}
            style={{ whiteSpace: 'pre-wrap' }}
          />
        ),
      )}
    </div>
  );
}
